import { IEvent, ITrackEntry } from "@pixi-spine/base";
import { Components } from "appworks/components/components";
import { PromptComponent } from "appworks/components/prompt/prompt-component";
import { PromptContentSubcomponent } from "appworks/components/prompt/prompt-content-subcomponent";
import { gameState } from "appworks/model/game-state";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { SoundEvent } from "appworks/services/sound/sound-events";
import { SoundService } from "appworks/services/sound/sound-service";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { Contract } from "appworks/utils/contracts/contract";
import { logger } from "appworks/utils/logger";
import { Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";
import { SlingoCompletionReason } from "slingo/integration/slingo-schema";
import { SlingoGameProgressResult } from "slingo/model/results/slingo-game-progress-result";
import { SlingoSoundEvent } from "slingo/sound/slingo-sound-events";
import { SlingoGameOverState } from "slingo/states/slingo-game-over-state";
import { BigWinComponent } from "slotworks/components/bigwin/big-win-component";

declare const __DEBUG__: boolean;
type gameSummaryScenes = keyof typeof gameLayers.Prompts.scene;

export class FowlGameOverState extends SlingoGameOverState {
    public onExit(): void {
        super.onExit();
        Services.get(SoundService).customEvent("results_end");
        Services.get(SoundService).event(SoundEvent.start_game);
    }

    protected showNoWinSummary(): Contract {
        Services.get(SoundService).customEvent("end_base_music");
        return super.showNoWinSummary();
    }

    // Need to copy over all of the text unfortunately since there's no other way to override the text fields
    protected showWinSummary(): Contract<void> {
        Services.get(SoundService).customEvent(SlingoSoundEvent.end_game_win);

        const translations = Services.get(TranslationsService);
        const currency = Services.get(CurrencyService);

        const gameplay = gameState.getCurrentGame();
        const gameProgress = gameplay.getLatestResultOfType(SlingoGameProgressResult);
        const totalWin = gameProgress.totalWin;
        const totalStake = gameplay.getTotalWagered();
        const meetsWinRequirement = this.meetsWinRequirement();

        if (__DEBUG__ && gameplay.getTotalWin() !== gameProgress.totalWin) {
            debugger;
        }

        const isBigWin = Components.get(BigWinComponent).isBigWin(totalWin, totalStake);
        const isFullHouse = gameplay.getLatestResultOfType(SlingoGameProgressResult).completionReason === SlingoCompletionReason.TICKET_MATCHED;

        /** Title */
        let titleKey = "results_caps";
        let pref = "result";
        if (isFullHouse && meetsWinRequirement) {
            // titleKey = "full_house_caps";
            pref = "megawin";
        } else if (isBigWin && meetsWinRequirement) {
            // titleKey = "big_win_caps";
            pref = "bigwin";
        } else if (meetsWinRequirement) {
            // titleKey = "win_caps";
            pref = "win";
        }

        // create the prompt
        const summaryPrompt = new PromptContentSubcomponent(["gamesummary_win"], 5000);

        summaryPrompt.setText("title", translations.get(titleKey));
        summaryPrompt.setText("win_breakdown", this.getWinBreakdown());
        summaryPrompt.setText("total_win", currency.format(gameProgress.totalWin));

        gameLayers.Prompts.onSceneEnter.addOnce((scene: gameSummaryScenes) => this.onSceneEnter(scene));
        gameLayers.Prompts.afterSceneEnter.addOnce((scene: gameSummaryScenes) => this.afterSceneEnter(scene, pref));

        return Components.get(PromptComponent).showPrompt(summaryPrompt);
    }

    // Animate celebration labels
    protected onSceneEnter(scene: gameSummaryScenes): void {
        if (scene !== "gamesummary_win") {
            return;
        }
        const { frame, title, total_win, total_win_label, win_breakdown } = gameLayers.Prompts.scene.gamesummary_win.contents;
        // hide total win text while transitioning in
        title.alpha = 0;
        total_win.alpha = 0;
        total_win_label.alpha = 0;
        win_breakdown.alpha = 0;
        // pulse the title
        const pulseTweens = [
            new Tween(title.landscape.scale)
                .to({ x: 1.1, y: 1.1 }, 200)
                .yoyo(true)
                .repeat(Infinity)
                .start(),
            new Tween(title.portrait.scale)
                .to({ x: 1.1, y: 1.1 }, 200)
                .yoyo(true)
                .repeat(Infinity)
                .start(),
        ];
        // fade back in once event fires
        frame.onEvent.addOnce((_entry: ITrackEntry, event: IEvent) => {
            if (event.data.name !== "prize") {
                return;
            }
            new Tween(total_win).to({ alpha: 1 }, 200).start();
            new Tween(total_win_label).to({ alpha: 1 }, 200).start();
            new Tween(win_breakdown).to({ alpha: 1 }, 200).start();
            // stop pulsing title
            while (pulseTweens.length > 0) {
                pulseTweens.pop().stop();
            }
        });
    }

    // Queue up barn idle animation after 'in'
    protected afterSceneEnter(scene: gameSummaryScenes, pref: string): void {
        if (scene !== "gamesummary_win") {
            return;
        }
        const { frame } = gameLayers.Prompts.scene.gamesummary_win.contents;
        frame.play(`${pref}_idle`);
        frame.spines.landscape.stateData.defaultMix = 1;
        frame.spines.portrait.stateData.defaultMix = 1;
    }

    protected getWinBreakdown(): string {
        const translations = Services.get(TranslationsService);
        const currency = Services.get(CurrencyService);

        const gameplay = gameState.getCurrentGame();
        const gameProgress = gameplay.getLatestResultOfType(SlingoGameProgressResult);

        let breakdownText = "";

        const bonusTotal = gameProgress.patternWin;
        const jackpotTotal = gameProgress.jackpotWin;
        const scatterTotal = gameProgress.symbolWin;

        if (bonusTotal > 0) {
            breakdownText += `${translations.get("results_bonus")}: ${currency.format(bonusTotal)}`;
        }
        if (scatterTotal > 0) {
            breakdownText += breakdownText.length > 0 ? "\n" : "";
            breakdownText += `${translations.get("results_scatters")}: ${currency.format(scatterTotal)}`;
        }
        // if (jackpotTotal > 0) {
        //     breakdownText += breakdownText.length > 0 ? "\n" : "";
        //     breakdownText += `${translations.get("results_jackpot")}: ${currency.format(jackpotTotal)}`;
        // }

        logger.log(`BREAKDOWN STRING: (total ${currency.format(gameProgress.totalWin)})`);
        logger.log(breakdownText);

        return breakdownText;
    }
}
